import React, { useEffect, useState } from "react";
import { Navbar, Nav, Container, Button, Dropdown } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../../App.css";
import Logo from "../common/Logo";
import UserAvatar from "../common/UserAvatar";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
	setIsMobile,
	setNavbarExpanded,
	setShowLoginModal,
	toggleNavbar,
} from "../../store/slices/userSlice";
import { authenticate, logoutThunk } from "../../store/thunks/authThunk";
import LoginSignupModal from "../Login/LoginModal";
import TermsAndConditionsModal from "../Login/TermsAndConditionsModal";

const TERMS_EXCLUDED_PAGES = [
	"editprofile",
	"rankings",
	"privacy-policy",
	"termsandconditions",
	"contact",
	"about",
	"watch",
];

const NavbarComponent = () => {
	const { isLoggedIn, user, navbarExpanded } = useAppSelector(
		(state) => state.user,
	);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const [isMobileViewport, setIsMobileViewport] = useState(false);
	const [showAgreementModal, setShowAgreementModal] = useState(false);

	const expanded = isMobileViewport ? navbarExpanded : false;

	useEffect(() => {
		const handleResize = () => {
			const mobile = window.innerWidth <= 991;
			setIsMobileViewport(mobile);
			dispatch(setIsMobile(window.innerWidth < 768));
			if (!mobile) {
				dispatch(setNavbarExpanded(false));
			}
		};

		handleResize();
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, [dispatch]);

	useEffect(() => {
		dispatch(authenticate());
	}, [dispatch]);

	useEffect(() => {
		const currentPath = window.location.pathname.toLowerCase().substring(1);
		if (user?.email && user.agreeToTerms === false) {
			if (!isLoggedIn) {
				setShowAgreementModal(false);
				return;
			}

			if (TERMS_EXCLUDED_PAGES.some((page) => currentPath.includes(page))) {
				setShowAgreementModal(false);
				return;
			}

			setShowAgreementModal(true);
			return;
		}

		setShowAgreementModal(false);
	}, [user, isLoggedIn]);

	const closeMobileNav = () => {
		if (isMobileViewport) {
			dispatch(setNavbarExpanded(false));
		}
	};

	const openLoginModal = () => {
		dispatch(setShowLoginModal(true));
	};

	const handleLogout = () => {
		dispatch(logoutThunk());
		closeMobileNav();
	};

	return (
		<Navbar
			expand="lg"
			className="main-navbar"
			expanded={expanded}
		>
			<Container fluid className="main-navbar__container px-2 px-sm-3 px-lg-4">
				<Navbar.Brand
					as={Link}
					to="/"
					className="main-navbar__brand"
					onClick={closeMobileNav}
				>
					<Logo />
				</Navbar.Brand>
				<Navbar.Toggle
					aria-controls="main-navbar-nav"
					className="main-navbar__toggle"
					onClick={() => dispatch(toggleNavbar())}
				/>
				<Navbar.Collapse id="main-navbar-nav" className="main-navbar__collapse">
					<Nav className="main-navbar__links me-auto">
						<Nav.Link
							as={Link}
							to="/"
							onClick={closeMobileNav}
							className={`main-navbar__link ${location.pathname === "/" ? "active" : ""}`}
						>
							Home
						</Nav.Link>
						<Nav.Link
							as={Link}
							to="/rankings"
							onClick={closeMobileNav}
							className={`main-navbar__link ${location.pathname === "/rankings" ? "active" : ""}`}
						>
							Rankings
						</Nav.Link>
						<Nav.Link
							as={Link}
							to="/schedule"
							onClick={closeMobileNav}
							className={`main-navbar__link ${location.pathname === "/schedule" ? "active" : ""}`}
						>
							Schedule
						</Nav.Link>
						<Nav.Link
							as={Link}
							to="/riders"
							onClick={closeMobileNav}
							className={`main-navbar__link ${location.pathname.startsWith("/riders") ? "active" : ""}`}
						>
							Riders
						</Nav.Link>
						<Nav.Link
							as={Link}
							to="/news"
							onClick={closeMobileNav}
							className={`main-navbar__link ${location.pathname.startsWith("/news") ? "active" : ""}`}
						>
							News
						</Nav.Link>
						<Nav.Link
							as={Link}
							to="/fantasy"
							onClick={closeMobileNav}
							className={`main-navbar__link ${location.pathname.startsWith("/fantasy") ? "active" : ""}`}
						>
							Fantasy
						</Nav.Link>
						<Nav.Link
							as={Link}
							to="/howtoplay"
							onClick={closeMobileNav}
							className={`main-navbar__link ${location.pathname === "/howtoplay" ? "active" : ""}`}
						>
							Rules
						</Nav.Link>
						<Nav.Link
							as={Link}
							to="/watch"
							onClick={closeMobileNav}
							className={`main-navbar__link ${location.pathname === "/watch" ? "active" : ""}`}
						>
							Watch
						</Nav.Link>
						{isLoggedIn && user?.role === "admin" && (
							<Nav.Link
								as={Link}
								to="/admin"
								onClick={closeMobileNav}
								className={`main-navbar__link ${location.pathname.startsWith("/admin") ? "active" : ""}`}
							>
								Admin
							</Nav.Link>
						)}
					</Nav>
					<div className="main-navbar__profile ms-lg-3 mt-3 mt-lg-0">
						{isLoggedIn ? (
							<Dropdown align="end">
								<Dropdown.Toggle
									variant="link"
									id="navbar-user-menu"
									className="main-navbar__profile-toggle"
								>
									<UserAvatar
										avatarId={user?.avatarId}
										size={24}
										className="me-2"
									/>
									{user?.username || "User"}
								</Dropdown.Toggle>
								<Dropdown.Menu className="main-navbar__menu">
									<Dropdown.Item
										onClick={() => {
											navigate("/editprofile");
											closeMobileNav();
										}}
									>
										Profile
									</Dropdown.Item>
									<Dropdown.Item
										onClick={() => {
											navigate("/teams");
											closeMobileNav();
										}}
									>
										Teams
									</Dropdown.Item>
									<Dropdown.Divider />
									<Dropdown.Item onClick={handleLogout}>Logout</Dropdown.Item>
								</Dropdown.Menu>
							</Dropdown>
						) : (
							<Button
								variant="outline-light"
								className="main-navbar__login-btn"
								onClick={openLoginModal}
							>
								Login
							</Button>
						)}
					</div>
				</Navbar.Collapse>
			</Container>
			<LoginSignupModal />
			{showAgreementModal && (
				<TermsAndConditionsModal
					show={showAgreementModal}
					onHide={() => setShowAgreementModal(false)}
				/>
			)}
		</Navbar>
	);
};

export default NavbarComponent;
